/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
    Hello there! This is a demonstration of how to override a file from the base template.

    It's necessary that the module export interface remain consistent,
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/

import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'

// original value is 25
DEFAULT_LIMIT_VALUES[0] = 25
DEFAULT_SEARCH_PARAMS.limit = 32

// Cache time constants - imported from separate file to allow SSR usage
// (ssr.js can't import from this file due to ESM re-exports from retail-react-app)
export {
    MAX_CACHE_AGE_CMS,
    MAX_CACHE_AGE_S,
    MAX_CACHE_AGE_M,
    MAX_CACHE_AGE_L,
    MAX_CACHE_AGE_XL,
    STALE_WHILE_REVALIDATE_S,
    STALE_WHILE_REVALIDATE_M,
    STALE_WHILE_REVALIDATE_L,
    STALE_WHILE_REVALIDATE_XL
} from './cache-times-configs'

export const DEFAULT_LOCALE = 'en-GB'
export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'
export const THEME_COLOR = '#FFFFFF'

// Constants used for product searching.
export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS}

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 3
export const RECENT_SEARCH_KEY = 'recent-search-key'
export const RECENT_SEARCH_MIN_LENGTH = 3

export const TRENDING_SEARCH_CATEGORIES = [
    'grg_clothing_jeans',
    'grg_clothing_sweatsuits_hoodies_and_sweatshirts',
    'grg_clothing_tops'
]

export const SHIPPING_COUNTRY_CODES = [
    {value: 'CA', label: 'Canada'},
    {value: 'US', label: 'United States'},
    {value: 'GB', label: 'United Kingdom'},
    {value: 'UK', label: 'United Kingdom'}
]

export const VICKYAI_RECOMMENDATIONS = {
    PRODUCT_RECOMMENDATION: 'recommendations',
    PRODUCT_BOUGHT_TOGETHER: 'bought-together',
    PRODUCT_SIMILARS: 'similars'
}

export const FILTER_ACCORDION_STATE = 'filters-expanded-index'

export const PUBLIC_VARIABLES = [
    'ADDRESS_SUGGESTIONS_ENABLED',
    'ADDRESS_VALIDATION_ENABLED',
    'ADDRESS_SERVICES_DEBOUNCE_INTERVAL',
    'ADYEN_CLIENT_KEY',
    'ADYEN_USE_KLARNA_WIDGET',
    'LOAD_INITIAL_PRODUCTS_LENGTH',
    'WISHLIST_COUNT_ON_PRODUCT_TILE_RENDERING_ENABLED',
    'SHOW_BOPIS',
    'ADYEN_ENNVIRONMENT',
    'KLARNA_OSMSDK_ENV',
    'KLARNA_CLIENT_ID',
    'KLARNA_ENVIRONMENT',
    'KLARNA_CLIENT_ID',
    'SMS_OPTIN_LOYALTY_ENABLED'
]

export const SIZE_REFINEMENTS_FALLBACK = [
    'c_clothingSize',
    'c_bottomSize',
    'c_shoeSize',
    'c_shoeSize',
    'c_otherSize',
    'c_accessoriesSize'
]

export const SIZES = [
    {value: 'XXS', label: 'XXS'},
    {value: 'XS', label: 'XS'},
    {value: 'S', label: 'S'},
    {value: 'M', label: 'M'},
    {value: 'L', label: 'L'},
    {value: 'XL', label: 'XL'}
]

export const CAT_MENU_SITEMAP_NAV_SSR_DEPTH = 3

export const RESOURCE_BADGE_MAPPING = {
    'onlineexclusive': {
        id: 'badge.label.online_exclusive',
        defaultMessage: 'Online Exclusive'
    },
    'finalsale': {
        id: 'badge.label.final_sale',
        defaultMessage: 'Final Sale'
    },
    'new': {
        id: 'badge.label.new',
        defaultMessage: 'New'
    },
    'earlyaccess': {
        id: 'badge.label.early_access',
        defaultMessage: 'Early Access'
    },
    'elevee': {
        id: 'badge.label.elevee',
        defaultMessage: 'Elevee'
    },
    'limitededition': {
        id: 'badge.label.limited_edition',
        defaultMessage: 'Limited Edition'
    },
    'extendedlengths': {
        id: 'badge.label.extended_lengths',
        defaultMessage: 'Extended Lengths'
    },
    'premiumwool': {
        id: 'badge.label.premium_wool',
        defaultMessage: 'Premium Wool'
    },
    'luxelounge': {
        id: 'badge.label.luxe_lounge',
        defaultMessage: 'Luxe Lounge'
    },
    'collab': {
        id: 'badge.label.collab',
        defaultMessage: 'Collab'
    },
    'rsvp': {
        id: 'badge.label.rsvp',
        defaultMessage: 'RSVP'
    },
    'sculptd': {
        id: 'badge.label.sculptd',
        defaultMessage: "Sculpt'd"
    },
    'backinstock': {
        id: 'badge.label.back_in_stock',
        defaultMessage: "Back In Stock"
    },
    'toohotforsale': {
        id: 'badge.label.too_hot_for_sale',
        defaultMessage: "Too Hot For Sale"
    },
    'essentials': {
        id: 'badge.label.essentials',
        defaultMessage: "Essentiæls"
    },
    'comingsoon': {
        id: 'badge.label.coming_soon',
        defaultMessage: "Coming Soon"
    },
    'waterresistant': {
        id: 'badge.label.water_resistant',
        defaultMessage: "Water Resistant"
    }
}

export const KLARNA_LOGIN_URL = 'https://app.klarna.com/login'

export * from '@salesforce/retail-react-app/app/constants'
